import { UseFormRegister, FieldValues, DeepMap, FieldError, UseFormGetValues, UseFormSetValue, Control } from 'react-hook-form';
import { StudentData, TSelectOption } from '../../util/types';
import Select from './Fields/Select';
import TextInput from './Fields/TextInput';
import schoolsList from '../schools.json';
import provinces from '../provinces.json';
import stringsToOptions from '../../util/stringsToSelectOptions';
import splitStudentsFromFormData from './splitStudentsFromFormData';
import Typeahead from './Fields/Typeahead';
import DateInput from './Fields/DateInput';

type Props = {
  studentIndex: number; // (0 is group leader)
  reviewMode: boolean;
  returnToStep?: () => void;
  removeStudent: (studentIndex: number) => void;

  register: UseFormRegister<FieldValues>;
  getValues: UseFormGetValues<FieldValues>;
  setValue: UseFormSetValue<FieldValues>;
  control: Control<FieldValues>;
  errors: DeepMap<FieldValues, FieldError>;
  formData: any;
  studentData: StudentData;
  isGroup: boolean;
};

/**
 * A StudentInfo component
 * @param props
 */
// TODO: put the rest of the student fields in here
const StudentInfo = (props: Props) => {
  const heading = props.studentIndex === 0
    ? 'Group leader'
    : `Group member ${props.studentIndex + 1}`;

  return (
    <fieldset className="StudentInfo Fieldset Fieldset--two-col">
      { props.isGroup
        // TODO: use heading tag...
        ? <legend className="StudentInfo__heading">{heading}</legend>
        : undefined
      }
      <TextInput
        name={`student_${props.studentIndex}_firstName`}
        label="First Name"
        defaultValue={props.studentData.firstName}
        validationObj={{required: 'This field is required'}}
        formData={props.formData}
        showEditBtn={props.reviewMode ? true : false}
        editable={props.reviewMode ? false : true}
        register={props.register}
        errors={props.errors}
        returnToStep={props.returnToStep}
      />

      <TextInput
        name={`student_${props.studentIndex}_lastName`}
        label="Last Name"
        defaultValue={props.studentData.lastName}
        validationObj={{required: 'This field is required'}}
        formData={props.formData}
        showEditBtn={props.reviewMode ? true : false}
        editable={props.reviewMode ? false : true}
        register={props.register}
        errors={props.errors}
        returnToStep={props.returnToStep}
      />

      <TextInput
        name={`student_${props.studentIndex}_email`}
        label="Email"
        disclaimer={
          <p className='TextInput__disclaimer'>
            <span>
              Don't have an email address? If you are 13+ years old, you can create a free Gmail account <a href="https://gmail.com" target="_blank" rel="noopener noreferrer">here</a>.
            </span>
          </p>
        }
        type="email"
        defaultValue={props.studentData.email}
        validationObj={{
          required: 'This field is required',
          validate: (value: string) => {
            // Need to grab the live data via getValues() or it doesn't update when you change the email
            const [,students] = splitStudentsFromFormData(props.getValues());
            // console.log('students', students);
            const emails = students?.map((student: StudentData) => student.email);
            // count how many times the email is used
            const emailCount = emails?.filter((email: string) => email === value).length;
            // console.log('emailCount', emailCount);
            if (emailCount > 1) return 'This email address is already in use';
            return true;
          }
        }}
        formData={props.formData}
        showEditBtn={props.reviewMode ? true : false}
        editable={props.reviewMode ? false : true}
        register={props.register}
        errors={props.errors}
        returnToStep={props.returnToStep}
      />

      <TextInput
        name={`student_${props.studentIndex}_confirmEmail`}
        label="Confirm Email"
        type="email"
        defaultValue={props.studentData.email}
        validationObj={{ validate: {
          emailEqual: (value: string) => (value === props.getValues()[`student_${props.studentIndex}_email`]) || 'Email confirmation error!',
        }}}
        formData={props.formData}
        showEditBtn={props.reviewMode ? true : false}
        editable={props.reviewMode ? false : true}
        register={props.register}
        errors={props.errors}
        returnToStep={props.returnToStep}
      />

      <TextInput
        name={`student_${props.studentIndex}_phone`}
        label="Phone number"
        type="tel"
        defaultValue={props.studentData.phone}
        validationObj={{required: 'This field is required'}}
        formData={props.formData}
        showEditBtn={props.reviewMode ? true : false}
        editable={props.reviewMode ? false : true}
        register={props.register}
        errors={props.errors}
        returnToStep={props.returnToStep}
      />

      <Typeahead
        name={`student_${props.studentIndex}_school`}
        label="School"
        defaultValue={props.studentData.school}
        validationObj={{required: 'This field is required'}}
        formData={props.formData}
        showEditBtn={props.reviewMode ? true : false}
        editable={props.reviewMode ? false : true}
        control={props.control}
        setValue={props.setValue}
        errors={props.errors}
        returnToStep={props.returnToStep}
        options={stringsToOptions(schoolsList)}
        optionsUrl="/api/schools"
        disclaimer={<p>If your school is not listed, please contact <a href="mailto:YCF@bepwr.ca?subject=YCF%20application%20missing%20my%20school">YCF@bepwr.ca</a></p>}
      />

      <Select
        name={`student_${props.studentIndex}_grade`}
        label="Grade"
        defaultValue={props.studentData.grade}
        validationObj={{required: 'This field is required'}}
        formData={props.formData}
        showEditBtn={props.reviewMode ? true : false}
        editable={props.reviewMode ? false : true}
        register={props.register}
        setValue={props.setValue}
        errors={props.errors}
        returnToStep={props.returnToStep}
        options={stringsToOptions(['Grade 5','Grade 6','Grade 7','Grade 8','Grade 9','Grade 10','Grade 11','Grade 12'])}
      />

      <DateInput
        name={`student_${props.studentIndex}_dob`}
        label="Date of Birth"
        defaultValue={props.studentData.dob}
        validationObj={{
          required: 'This field is required',
          max: new Date().toISOString().split('T')[0],
        }}
        formData={props.formData}
        showEditBtn={props.reviewMode ? true : false}
        editable={props.reviewMode ? false : true}
        register={props.register}
        errors={props.errors}
        returnToStep={props.returnToStep}
      />

      <TextInput
        name={`student_${props.studentIndex}_streetAddress`}
        label="Street address"
        defaultValue={props.studentData.streetAddress}
        validationObj={{required: 'This field is required'}}
        formData={props.formData}
        showEditBtn={props.reviewMode ? true : false}
        editable={props.reviewMode ? false : true}
        register={props.register}
        errors={props.errors}
        returnToStep={props.returnToStep}
      />
      <TextInput
        name={`student_${props.studentIndex}_city`}
        label="City"
        defaultValue={props.studentData.city}
        validationObj={{required: 'This field is required'}}
        formData={props.formData}
        showEditBtn={props.reviewMode ? true : false}
        editable={props.reviewMode ? false : true}
        register={props.register}
        errors={props.errors}
        returnToStep={props.returnToStep}
      />
      <TextInput
        name={`student_${props.studentIndex}_postalCode`}
        label="Postal code"
        defaultValue={props.studentData.postalCode}
        validationObj={{required: 'This field is required'}}
        formData={props.formData}
        showEditBtn={props.reviewMode ? true : false}
        editable={props.reviewMode ? false : true}
        register={props.register}
        errors={props.errors}
        returnToStep={props.returnToStep}
      />
      <Select
        name={`student_${props.studentIndex}_province`}
        label="Province"
        defaultValue={props.studentData.province}
        validationObj={{required: 'This field is required'}}
        formData={props.formData}
        showEditBtn={props.reviewMode ? true : false}
        editable={props.reviewMode ? false : true}
        register={props.register}
        errors={props.errors}
        setValue={props.setValue}
        returnToStep={props.returnToStep}
        options={Object.entries(provinces).reduce((accum, [provCode, provName]) => {
          accum.push({
            label: provName,
            value: provCode,
          });
          return accum;
        }, [] as TSelectOption[])}
      />

      <TextInput
        name={`student_${props.studentIndex}_uuid`}
        label="UUID"
        type="hidden"
        defaultValue={props.studentData.uuid}
        formData={props.formData}
        showEditBtn={false}
        editable={true} // getValues doesn't return anything if it's disabled
        register={props.register}
        errors={props.errors}
        returnToStep={props.returnToStep}
      />

      { props.reviewMode || !props.isGroup || props.studentIndex === 0
        ? null
        : <div className="RemoveStudentButton">
            <button onClick={(e) => {
              e.preventDefault();
              props.removeStudent(props.studentIndex);
            }}>
              Remove member
            </button>
          </div>
      }
    </fieldset>
  );
};

export default StudentInfo;
